/**
 * SA-only Channel Programming preview queue.
 * Top-down one song per rotation, then wrap (C2 §4.1).
 *
 * Least-played selection is O(tracks in this rotation) — typically dozens —
 * never a scan of global play history. At platform scale (hundreds of millions
 * of plays/day), the durable path is atomic per-(rotation, track) counters
 * (increment on play) + the same O(n_rotation) pick; not querying play events.
 *
 * Counts are kept relative (renormalized) so they never need to grow into the
 * hundreds of millions for selection math.
 *
 * End-user / player / Master Account playback is Production-only elsewhere.
 */

export type ChannelPreviewSource = "staging" | "production";

export interface PreviewRotationTrack {
  trackId: string;
  label: string;
}

export interface PreviewRotation {
  id: string;
  /** Left-column slot, e.g. Rotation A01 */
  slotLabel: string;
  categoryLabel: string;
  tracks: PreviewRotationTrack[];
}

export interface PreviewPick {
  rotationId: string;
  slotLabel: string;
  categoryLabel: string;
  trackId: string;
  label: string;
}

/** playCountByTrackId: trackId → relative plays within its rotation. */
export type PlayCountMap = Record<string, number>;

/** When every track’s count is at least this, subtract the min (relative only). */
export const PLAY_COUNT_RENORM_FLOOR = 10_000;

/**
 * Ensure every track in the rotation has a count. New tracks inherit the
 * current least-played count so they enter the rotation evenly (owner rule).
 */
export function syncRotationPlayCounts(
  rotation: PreviewRotation,
  playCounts: PlayCountMap,
): PlayCountMap {
  const tracks = rotation.tracks.filter((t) => t.trackId);
  if (tracks.length === 0) return playCounts;
  const next = { ...playCounts };
  const existing = tracks
    .map((t) => next[t.trackId])
    .filter((n): n is number => typeof n === "number" && Number.isFinite(n));
  const least =
    existing.length === 0 ? 0 : existing.reduce((a, b) => Math.min(a, b), existing[0]);
  for (const t of tracks) {
    if (next[t.trackId] == null || !Number.isFinite(next[t.trackId])) {
      next[t.trackId] = least;
    }
  }
  return next;
}

/**
 * Keep counters small for selection: subtract min when all tracks are above floor.
 * Relative order is preserved.
 */
export function renormalizePlayCounts(
  rotation: PreviewRotation,
  playCounts: PlayCountMap,
  floor: number = PLAY_COUNT_RENORM_FLOOR,
): PlayCountMap {
  const tracks = rotation.tracks.filter((t) => t.trackId);
  if (tracks.length === 0) return playCounts;
  const values = tracks.map((t) => playCounts[t.trackId] ?? 0);
  const min = values.reduce((a, b) => Math.min(a, b), values[0]);
  if (min < floor) return playCounts;
  const next = { ...playCounts };
  for (const t of tracks) {
    next[t.trackId] = (next[t.trackId] ?? 0) - min;
  }
  return next;
}

/**
 * Pick the least-played track in the rotation. Prefer not repeating the last
 * track when multiple share the same least count.
 */
export function pickTrackForRotation(
  rotation: PreviewRotation,
  playCounts: PlayCountMap,
  lastTrackId?: string | null,
): PreviewRotationTrack | null {
  const tracks = rotation.tracks.filter((t) => t.trackId);
  if (tracks.length === 0) return null;
  if (tracks.length === 1) return tracks[0];

  const synced = syncRotationPlayCounts(rotation, playCounts);
  let least = Infinity;
  for (const t of tracks) {
    const c = synced[t.trackId] ?? 0;
    if (c < least) least = c;
  }
  const candidates = tracks.filter((t) => (synced[t.trackId] ?? 0) === least);
  if (candidates.length === 1) return candidates[0];
  if (lastTrackId) {
    const notLast = candidates.filter((t) => t.trackId !== lastTrackId);
    if (notLast.length > 0) return notLast[0];
  }
  return candidates[0];
}

export function recordPlay(
  rotation: PreviewRotation,
  playCounts: PlayCountMap,
  trackId: string,
): PlayCountMap {
  const synced = syncRotationPlayCounts(rotation, playCounts);
  const next = {
    ...synced,
    [trackId]: (synced[trackId] ?? 0) + 1,
  };
  return renormalizePlayCounts(rotation, next);
}

/**
 * Advance from `fromIndex` (exclusive) to the next rotation with tracks.
 * Uses least-played within each rotation.
 */
export function nextPreviewPick(
  rotations: PreviewRotation[],
  fromIndex: number,
  playCountsByRotation: Readonly<Record<string, PlayCountMap>>,
  lastTrackIdByRotation: Readonly<Record<string, string | undefined>>,
): { pick: PreviewPick; index: number } | null {
  if (!rotations.length) return null;
  const start = fromIndex < 0 ? 0 : fromIndex + 1;
  for (let step = 0; step < rotations.length; step += 1) {
    const index = (start + step) % rotations.length;
    const rotation = rotations[index];
    const track = pickTrackForRotation(
      rotation,
      playCountsByRotation[rotation.id] || {},
      lastTrackIdByRotation[rotation.id],
    );
    if (!track) continue;
    return {
      index,
      pick: {
        rotationId: rotation.id,
        slotLabel: rotation.slotLabel,
        categoryLabel: rotation.categoryLabel,
        trackId: track.trackId,
        label: track.label,
      },
    };
  }
  return null;
}

/** First playable pick starting at the top of the schedule (least-played in R01). */
export function firstPreviewPick(
  rotations: PreviewRotation[],
  playCountsByRotation: Readonly<Record<string, PlayCountMap>> = {},
  lastTrackIdByRotation: Readonly<Record<string, string | undefined>> = {},
): { pick: PreviewPick; index: number } | null {
  return nextPreviewPick(
    rotations,
    -1,
    playCountsByRotation,
    lastTrackIdByRotation,
  );
}

export function hasPlayableTracks(rotations: PreviewRotation[]): boolean {
  return rotations.some((r) => r.tracks.some((t) => t.trackId));
}

export function storageKeyForPreviewCounts(
  channelId: string,
  source: ChannelPreviewSource,
): string {
  return `kk:channelPreviewPlayCounts:v1:${channelId}:${source}`;
}
